// @ts-strict-ignore
import { Trans, useTranslation } from "react-i18next";

import { Button, Toggle, Typography } from "@aviary";
import type { IngredientFilterV2 } from "@shared/components/CatalogSearch/CatalogSearch";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";

import { CommonIngredients } from "../CommonIngredients/CommonIngredients";
import { IngredientSearch } from "../IngredientSearch/IngredientSearch";

import { IngredientDosage } from "./IngredientDosage/IngredientDosage";

import * as styles from "./IncludeIngredients.styles";

interface Props {
  logFilterEvent: (entityName: string, entityType: string) => void;
  isContextual?: boolean;
}

const IncludeIngredientsContent = ({ logFilterEvent, isContextual = false }: Props) => {
  const { t } = useTranslation();

  const { activeIngredients, onlyIngredient, setActiveIngredients, setOnlyIngredient } =
    useCatalogSearch();

  const isIngredientIncluded = ingredientId =>
    activeIngredients.some(
      includedIngredient => includedIngredient?.searchAttributes?.id === ingredientId
    );

  const onIncludeIngredient = (ingredientId, ingredientName) => {
    const distanceFromTop = window?.scrollY;
    if (isIngredientIncluded(ingredientId)) {
      setActiveIngredients([
        ...(activeIngredients as IngredientFilterV2[])?.filter(
          includedIngredient => includedIngredient.searchAttributes.id !== ingredientId
        ),
      ]);
    } else {
      const ingredientToInclude = {} as IngredientFilterV2;
      ingredientToInclude.name = ingredientName;
      ingredientToInclude.searchAttributes = {
        id: ingredientId,
      };
      logFilterEvent(ingredientName, FilterType.INCLUDE_INGREDIENT);

      setActiveIngredients([...activeIngredients, ingredientToInclude]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const onTogglingOnlyIngredient = () => {
    const distanceFromTop = window?.scrollY;
    setOnlyIngredient(!onlyIngredient);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderOnlyIngredientToggle = () => {
    if (activeIngredients.length === 0) {
      return null;
    }

    return (
      <div css={styles.onlyIngredientToggle}>
        <Typography>
          <Trans i18nKey={l.sharedCatalog.filter.IngredientToggleText}>
            Include <span css={styles.bold}>only</span> these ingredients:
          </Trans>
        </Typography>
        <Toggle isOn={onlyIngredient} onClick={onTogglingOnlyIngredient} />
      </div>
    );
  };

  const onClearAllIngredients = () => {
    const distanceFromTop = window?.scrollY;
    setActiveIngredients([]);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderClearAllIngredients = () => {
    if (activeIngredients.length === 0) {
      return null;
    }

    return (
      <div css={styles.clearAll}>
        <Button
          aria-label={t(l.sharedCommon.ClearAllSearchFilters)}
          isText
          isColor="info"
          onClick={onClearAllIngredients}
        >
          {t(l.sharedCommon.Clearall)}
        </Button>
      </div>
    );
  };

  const renderIngredientDosages = activeIngredients.map(ingredient => (
    <IngredientDosage key={ingredient.searchAttributes.id} ingredient={ingredient} />
  ));

  return (
    <>
      <IngredientSearch
        isIngredientSelected={isIngredientIncluded}
        onSelectIngredient={onIncludeIngredient}
        isContextual={isContextual}
      />

      {!isContextual && (
        <CommonIngredients
          isIngredientSelected={isIngredientIncluded}
          selectedIngredients={activeIngredients}
          onSelectIngredient={onIncludeIngredient}
        />
      )}

      {renderOnlyIngredientToggle()}

      {renderIngredientDosages}

      {renderClearAllIngredients()}
    </>
  );
};

export { IncludeIngredientsContent };
